// =============================================================================
// RoleGroupSection: one era on the corpus canvas — the role header (company,
// title, dates) + its achievement cards, and the native-HTML5-drag drop target
// a card lands on to re-home. Mirrors apps/web's per-era section (RoleSection,
// Corpus.tsx:1317+) in STRUCTURE (header, card list, a drop affordance) without
// pulling in that component's full Studio-live surface (voice flights, gold
// edges) — none of that is this brief's scope; this is the static
// (non-voice-anchored) card canvas + drag-rehome only.
//
// WIRING TARGET #1 (résumé display) adds the per-era "read as résumé" toggle,
// ported 1:1 from apps/web's asResume/onToggleResume/RoleResumeSection pattern
// (Corpus.tsx:1019-1021, 1412-1427, 1514-1523): a Cards <-> Résumé button that
// flips this era between the card grid (unchanged) and a RoleResumeSection
// fragment (role-header + ordered ghostwritten bullets, the same .rd-* look as
// the full résumé). Cards are never removed — one toggle-click away. This is
// also the fix for issue #44 (internal provenance tags leaking as chips):
// RoleResumeSection never reads `.tags` at all, so an era read as résumé is
// guaranteed provenance-free regardless of what a card's tags array carries.
//
// LANE ROLE-REFINE adds the role edit affordance (parity matrix gap #6): a
// pencil button beside the résumé toggle flips the header into RoleEditForm
// in place (no modal, no navigation away). Only rendered for a real role (the
// orphan "Unattached" bucket has no role row to edit, same reasoning
// canToggleResume already applies). Also threads onSaveEdit/savingEdit down
// to each AchievementCard so gap #7 (achievement field editing) lands from
// the same parent.
// =============================================================================

import { useState } from 'react';
import type { ResumeRoleBullet } from '@careeropt/schema';
import { AchievementCard } from './AchievementCard';
import { Card } from './Card';
import clsx from './clsx';
import { RoleEditForm } from './RoleEditForm';
import { RoleResumeSection } from './resume/RoleResumeSection';
import styles from './RoleGroupSection.module.css';
import type { AchievementPatch, AchievementRecord, RolePatch, RoleRecord } from '../lib/api';

export interface RoleGroupSectionProps {
  /** null = the "Unattached" bucket (achievements with no role yet). */
  role: RoleRecord | null;
  items: AchievementRecord[];
  draggingCardId: string | null;
  pendingCardId: string | null;
  /** True while THIS section is the current drag-over target (drag-target
   *  visual state — an era a drop here would move the card into). */
  isDropTarget: boolean;
  onDragStartCard: (id: string) => void;
  onDragEndCard: () => void;
  onDragOverSection: () => void;
  onDragLeaveSection: () => void;
  /** Fires when a card is dropped on this section — the caller resolves
   *  whether it is a no-op (dropped on its own era) or a real re-home. */
  onDropCard: (cardId: string, targetRoleId: string | null) => void;
  /** True while this era is toggled to read AS a résumé fragment instead of
   *  the card grid. Defaults false (every era opens on the card grid, exactly
   *  as before this brief) — the orphan "Unattached" section has no toggle
   *  (mirrors apps/web: the résumé format has no home for role-less bullets). */
  asResume?: boolean;
  onToggleResume?: () => void;
  /** The SERVED ghostwritten bullets for THIS era (sliced from the corpus-
   *  résumé document by the parent) — undefined when the document hasn't
   *  loaded yet or has no matching role; RoleResumeSection falls back to each
   *  card's verbatim headline in that case. */
  servedBullets?: ResumeRoleBullet[];
  /** Present -> this role's own fields can be edited (the pencil button
   *  renders). Absent -> read-only, byte-identical to before this lane.
   *  Returns the honest error message on failure, or null on success — same
   *  contract as useCorpusCanvas's rehome()/editRole(). */
  onSaveRoleEdit?: (roleId: string, patch: RolePatch) => Promise<string | null>;
  /** True while THIS role's field-edit save is in flight. */
  savingRoleEdit?: boolean;
  /** Present -> every achievement card in this section can be edited (the
   *  pencil button renders per card). Threaded straight through to each
   *  AchievementCard's own onSaveEdit prop. */
  onSaveAchievementEdit?: (achievementId: string, patch: AchievementPatch) => Promise<string | null>;
  /** The achievement id currently mid-save (its card shows the saving state),
   *  or null when no achievement edit is in flight. */
  savingAchievementId?: string | null;
}

function eraLabel(role: RoleRecord): string {
  const start = formatYearMonth(role.startDate);
  const end = role.isCurrent === 1 ? 'Present' : role.endDate ? formatYearMonth(role.endDate) : '';
  return end ? `${start} – ${end}` : start;
}

/** yyyy-mm-dd -> "Mon YYYY", using the browser's own locale (no AU/US hardcode
 *  — apps/web's equivalent formatDate hardcodes 'en-AU', which this project's
 *  locale rule flags as debt; passing no locale argument lets Intl fall back
 *  to the runtime's default instead of asserting one). */
function formatYearMonth(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(d);
}

export function RoleGroupSection({
  role,
  items,
  draggingCardId,
  pendingCardId,
  isDropTarget,
  onDragStartCard,
  onDragEndCard,
  onDragOverSection,
  onDragLeaveSection,
  onDropCard,
  asResume = false,
  onToggleResume,
  servedBullets,
  onSaveRoleEdit,
  savingRoleEdit = false,
  onSaveAchievementEdit,
  savingAchievementId = null,
}: RoleGroupSectionProps) {
  const targetRoleId = role?.id ?? null;
  const [editingRole, setEditingRole] = useState(false);
  // Mirrors apps/web's own gate exactly (Corpus.tsx:1406: `role && items.length > 0`)
  // — the orphan "Unattached" bucket and an empty era never show the toggle.
  const canToggleResume = role != null && items.length > 0 && onToggleResume != null;
  // Same "only a real role can be edited" gate — there is no row to PATCH for
  // the Unattached bucket.
  const canEditRole = role != null && onSaveRoleEdit != null;

  return (
    <section
      className={clsx(styles.section, isDropTarget && styles.dropTarget)}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        onDragOverSection();
      }}
      onDragLeave={onDragLeaveSection}
      onDrop={(e) => {
        e.preventDefault();
        const cardId = e.dataTransfer.getData('text/plain');
        if (cardId) onDropCard(cardId, targetRoleId);
      }}
    >
      {editingRole && role ? (
        <Card className={styles.roleEditFrame}>
          <RoleEditForm
            role={role}
            saving={savingRoleEdit}
            onSave={async (patch) => {
              const err = await onSaveRoleEdit!(role.id, patch);
              if (!err) setEditingRole(false);
              return err;
            }}
            onCancel={() => setEditingRole(false)}
          />
        </Card>
      ) : (
        <div className={styles.header}>
          <div className={styles.headerText}>
            <div className={styles.role}>{role ? role.title : 'Unattached'}</div>
            <div className={styles.meta}>
              {role ? `${role.company} · ${eraLabel(role)}` : 'Cards without a role yet'}
            </div>
          </div>
          {canEditRole ? (
            <button
              type="button"
              className={styles.roleEditButton}
              onClick={() => setEditingRole(true)}
              aria-label={`Edit ${role!.title} at ${role!.company}`}
              title="Edit this role"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          ) : null}
          {canToggleResume ? (
            <button
              type="button"
              className={styles.resumeToggle}
              onClick={onToggleResume}
              aria-pressed={asResume}
              title={asResume ? 'Back to cards' : 'Read this era as résumé'}
            >
              {asResume ? (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  <rect x="14" y="3" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  <rect x="3" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                  <rect x="14" y="14" width="7" height="7" rx="1.5" stroke="currentColor" strokeWidth="2" />
                </svg>
              ) : (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path d="M14 3v6h6" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                </svg>
              )}
              {asResume ? 'Cards' : 'Résumé'}
            </button>
          ) : null}
          <div className={styles.count}>{items.length}</div>
        </div>
      )}

      {editingRole ? null : asResume && role ? (
        // The era, read AS résumé: role-header + ordered bullets, the same look
        // as the printed CV. Cards are one toggle-click away; nothing removed.
        <Card className={styles.resumeFrame}>
          <RoleResumeSection role={role} items={items} servedBullets={servedBullets} />
        </Card>
      ) : items.length === 0 ? (
        <Card className={styles.emptyCard}>
          <div className={styles.emptyText}>
            No stories captured here yet — talk it through with Meira or drag one in.
          </div>
        </Card>
      ) : (
        <div className={styles.cardGrid}>
          {items.map((item) => (
            <AchievementCard
              key={item.id}
              achievement={item}
              dragging={draggingCardId === item.id}
              pending={pendingCardId === item.id}
              onDragStart={onDragStartCard}
              onDragEnd={onDragEndCard}
              onSaveEdit={onSaveAchievementEdit}
              savingEdit={savingAchievementId === item.id}
            />
          ))}
        </div>
      )}
    </section>
  );
}
